import { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Grid,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material'
import ShuffleRoundedIcon from '@mui/icons-material/ShuffleRounded'

import GridHeader from '../components/GridHeader'
import DialogGirone from '../components/DialogGirone'
import SelectionBar from '../components/selection/SelectionBar'
import ButtonUpload from '../components/ButtonUpload'
import ButtonDownload from '../components/ButtonDownload'
import ButtonRunDownload from '../components/ButtonRunDownload'
import SortableTableHead from '../components/SortableTableHead'
import { config } from '../settings/configs'
import { colours, GIRONI_FUNCTIONS } from '../settings/settings'
import { useFeedback } from '../hooks/feedback-hook'
import { sortNumerically } from '../hooks/sort-functions'
import { getCategorie, getGironi } from '../hooks/selection-get-functions'
import { mailTo } from '../hooks/utils-functions'
import {
  useGetAffiliazioniLightQuery,
  useGetImpostazioniAvanzateQuery,
  useGetIscrizioniQuery,
  useUpdateIscrizioneGironeMutation,
  useUpdateIscrizionePosizioneMutation,
} from '../hooks/api-slice'

const AVANZ_FIELDS = ['OpzioniIscrizione']

const Gironi = () => {
  const { auth, admin } = useSelector(state => state.home.infoUtente)
  const { data: avanzate } = useGetImpostazioniAvanzateQuery(AVANZ_FIELDS, {
    skip: !admin,
  })
  const { data: affiliazioniLight } = useGetAffiliazioniLightQuery(undefined, {
    skip: !admin,
  })
  const selectedCampionatoGironiPubblicati = useSelector(
    state => state.selection.selectedCampionatoGironiPubblicati,
  )
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const availableCategorie = useSelector(
    state => state.selection.availableCategorie,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )
  const availableGironi = useSelector(state => state.selection.availableGironi)
  const selectedGirone = useSelector(state => state.selection.selectedGirone)
  const selectedTeam = useSelector(state => state.selection.selectedTeam)
  const { data: iscrizioniGironi } = useGetIscrizioniQuery(
    { auth, campionato: selectedCampionato, iFilter: 'gironi' },
    {
      skip: !(
        selectedCampionato &&
        (admin || selectedCampionatoGironiPubblicati)
      ),
    },
  )

  const [updateIscrizioneGirone] = useUpdateIscrizioneGironeMutation()
  const [updateIscrizionePosizione] = useUpdateIscrizionePosizioneMutation()

  document.title = `${config?.name} - Gironi`

  const { setFeedback } = useFeedback()

  const { OpzioniIscrizione } = avanzate || {}
  const showAltreRichieste = OpzioniIscrizione?.includes('altre-richieste')

  let columnsGironi = [{ label: 'Squadra', sortable: false }]
  if (!admin)
    columnsGironi = [
      ...columnsGironi,
      { label: 'Gioca in casa', sortable: false },
    ]

  if (auth) {
    if (!admin)
      columnsGironi = [
        ...columnsGironi,
        { label: 'Referente', sortable: false },
        { label: 'Telefono', sortable: false },
        { label: 'Mail', sortable: false },
      ]

    if (admin) {
      columnsGironi = [
        { label: 'Pos.', sortable: false },
        ...columnsGironi,
        { label: 'Zona', sortable: false },
        { label: 'Abbin.', sortable: false },
        { label: 'Altern.', sortable: false },
        { label: 'Indisp.', sortable: false },
      ]
      if (showAltreRichieste)
        columnsGironi = [...columnsGironi, { label: 'Note', sortable: false }]
      columnsGironi = [...columnsGironi, { label: 'Opzioni', sortable: false }]
    }
  }

  // state to manage the change girone dialog
  const [openChangeGirone, setOpenChangeGirone] = useState()
  const [girIscrizione, setGirIscrizione] = useState()

  const handleClickOpenChangeGirone = id => {
    setGirIscrizione(iscrizioniGironi.find(i => i.id === id))
    setOpenChangeGirone(true)
  }

  const handleCloseChangeGirone = () => {
    setOpenChangeGirone()
    setGirIscrizione()
  }

  // change iscrizione girone and posizione
  const changeGirone = async (
    girIscrizione,
    selectedFunction,
    newGirone,
    newPosizione,
    newTeam,
    newGironeAdd,
    newPosizioneAdd,
  ) => {
    if (!girIscrizione?.id) {
      setFeedback('Nessuna squadra trovata!')
      return
    }

    if (!GIRONI_FUNCTIONS.includes(selectedFunction)) {
      setFeedback('Funzionalità non trovata!')
      return
    }

    try {
      // 'sposta' => swap teams by newGirone and newPosizione
      if (selectedFunction === GIRONI_FUNCTIONS[0]) {
        if (!newGirone || !newPosizione) {
          setFeedback('Mancano delle informazioni!')
          return
        }

        const arg1 = { id: girIscrizione?.id, girone: newGirone }
        const result1 = await updateIscrizioneGirone(arg1)
        if (result1?.error) throw new Error(result1?.error?.data?.message)

        const arg2 = { id: girIscrizione?.id, posizione: newPosizione }
        const result2 = await updateIscrizionePosizione(arg2)
        if (result2?.error) throw new Error(result2?.error?.data?.message)

        const newTeamID = iscrizioniGironi?.find(
          i =>
            i.codiceCampionato === girIscrizione.codiceCampionato &&
            i.categoria === girIscrizione.categoria &&
            i.girone === newGirone &&
            i.posizione === newPosizione,
        )?._id

        if (newTeamID) {
          const arg1 = { id: newTeamID, girone: girIscrizione?.girone }
          const result1 = await updateIscrizioneGirone(arg1)
          if (result1?.error) throw new Error(result1?.error?.data?.message)

          const arg2 = { id: newTeamID, posizione: girIscrizione?.posizione }
          const result2 = await updateIscrizionePosizione(arg2)
          if (result2?.error) throw new Error(result2?.error?.data?.message)
        }
      }

      // 'scambia' => swap teams by ID
      if (selectedFunction === GIRONI_FUNCTIONS[1]) {
        if (!newTeam?.id) {
          setFeedback('Mancano delle informazioni!')
          return
        }

        const arg1 = { id: girIscrizione?.id, girone: newTeam?.girone }
        const result1 = await updateIscrizioneGirone(arg1)
        if (result1?.error) throw new Error(result1?.error?.data?.message)

        const arg2 = { id: girIscrizione?.id, posizione: newTeam?.posizione }
        const result2 = await updateIscrizionePosizione(arg2)
        if (result2?.error) throw new Error(result2?.error?.data?.message)

        const arg3 = { id: newTeam?.id, girone: girIscrizione?.girone }
        const result3 = await updateIscrizioneGirone(arg3)
        if (result3?.error) throw new Error(result3?.error?.data?.message)

        const arg4 = { id: newTeam?.id, posizione: girIscrizione?.posizione }
        const result4 = await updateIscrizionePosizione(arg4)
        if (result4?.error) throw new Error(result4?.error?.data?.message)
      }

      // 'metti' => put team into a new girone
      if (selectedFunction === GIRONI_FUNCTIONS[2]) {
        if (!newGironeAdd || !newPosizioneAdd) {
          setFeedback('Mancano delle informazioni!')
          return
        }

        const arg1 = { id: girIscrizione?.id, girone: newGironeAdd }
        const result1 = await updateIscrizioneGirone(arg1)
        if (result1?.error) throw new Error(result1?.error?.data?.message)

        const arg2 = { id: girIscrizione?.id, posizione: newPosizioneAdd }
        const result2 = await updateIscrizionePosizione(arg2)
        if (result2?.error) throw new Error(result2?.error?.data?.message)
      }

      // 'togli' => remove team from gironi
      if (selectedFunction === GIRONI_FUNCTIONS[3]) {
        const arg1 = { id: girIscrizione?.id }
        const result = await updateIscrizioneGirone(arg1)
        if (result?.error) throw new Error(result?.error?.data?.message)

        const arg2 = { id: girIscrizione?.id }
        const result2 = await updateIscrizionePosizione(arg2)
        if (result2?.error) throw new Error(result2?.error?.data?.message)
      }

      handleCloseChangeGirone()
    } catch (err) {
      console.log(err)
      setFeedback(err.message)
    }
  }

  const GironiTable = ({ categoria, girone }) => {
    const gironeTable = iscrizioniGironi
      .filter(
        i =>
          i.categoria === categoria &&
          (!!girone ? i.girone === girone : !i.girone),
      )
      .sort((a, b) => sortNumerically(a.posizione, b.posizione))

    const gironeTitle = !!girone ? `Girone ${girone}` : 'Senza girone'

    return (
      <Grid size={12}>
        <h4>
          Categoria {categoria} - {gironeTitle}
        </h4>
        <TableContainer component={Paper}>
          <Table size='small'>
            <SortableTableHead table={gironeTable} columns={columnsGironi} />
            <TableBody>
              {gironeTable.map((a, index) => {
                return (
                  <TableRow
                    key={index}
                    style={{ backgroundColor: colours.white }}
                  >
                    {admin && (
                      <TableCell sx={{ width: '5vw' }}>{a.posizione}</TableCell>
                    )}
                    <TableCell sx={admin ? { width: '15vw' } : {}}>
                      {a.id === selectedTeam ? (
                        <b>{a.nomeSquadra}</b>
                      ) : (
                        a.nomeSquadra
                      )}
                    </TableCell>
                    {!admin && (
                      <TableCell>
                        {!!a.senzaPalestra
                          ? 'no'
                          : (a.giorno &&
                              a.orario &&
                              `${a.giorno} alle ${a.orario}`) ||
                            a.giorno ||
                            (a.orario && `alle ${a.orario}`) ||
                            ''}
                      </TableCell>
                    )}
                    {auth && !admin && <TableCell>{a.nomeReferente}</TableCell>}
                    {auth && !admin && (
                      <TableCell>{a.telefonoReferente}</TableCell>
                    )}
                    {auth && !admin && (
                      <TableCell>{mailTo(a.mailReferente)}</TableCell>
                    )}
                    {admin && (
                      <TableCell sx={{ width: '5vw' }}>
                        {
                          affiliazioniLight?.find(
                            aff => aff.codiceSocieta === a.codiceSocieta,
                          )?.zona
                        }
                      </TableCell>
                    )}
                    {admin && (
                      <TableCell sx={{ width: '5vw' }}>
                        {a.abbinamenti?.length || '-'}
                      </TableCell>
                    )}
                    {admin && (
                      <TableCell sx={{ width: '5vw' }}>
                        {a.alternanze?.length || '-'}
                      </TableCell>
                    )}
                    {admin && (
                      <TableCell sx={{ width: '5vw' }}>
                        {a.indisponibilitaPalestra?.length || '-'}
                      </TableCell>
                    )}
                    {admin && showAltreRichieste && (
                      <TableCell>{a.note}</TableCell>
                    )}
                    {admin && (
                      <TableCell sx={{ width: '5vw' }}>
                        <IconButton
                          style={{ color: colours.blueDark }}
                          onClick={() => handleClickOpenChangeGirone(a.id)}
                        >
                          <Tooltip title='Sposta'>
                            <ShuffleRoundedIcon />
                          </Tooltip>
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    )
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2>Gironi</GridHeader>
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar
          data={iscrizioniGironi}
          yCampionato
          yCategoria
          yGirone
          yTeam
        />
        {admin && (
          <>
            <Grid size={{ xs: 12, md: 3 }}>
              <ButtonUpload
                buttonText='Carica Gironi'
                algorithm='04_UpdateGironi'
                fileName='Input'
                runScript
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <ButtonDownload algorithm='04_UpdateGironi' type='TXT' />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <ButtonRunDownload
                buttonText='Scarica CSV'
                algorithm='01_GenerateTable'
                triggerDownload
                type='CSV'
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <ButtonRunDownload
                buttonText='Scarica Excel'
                algorithm='07_GenerateExcelGironi'
                type='XLSX'
                disabled={
                  !selectedCampionato ||
                  !selectedCategoria ||
                  selectedCategoria === availableCategorie[0]
                }
              />
            </Grid>
          </>
        )}
      </Grid>
      {(!admin && selectedCampionato && !selectedCampionatoGironiPubblicati && (
        <GridHeader text='Gironi non ancora pubblicati.' />
      )) || (
        <>
          {!!selectedCategoria &&
            ((!iscrizioniGironi?.length && (
              <GridHeader text='Nessun girone da visualizzare.' />
            )) || (
              <Grid container spacing={3} sx={{ width: '100%' }}>
                {getCategorie(
                  iscrizioniGironi.filter(i =>
                    !!selectedCategoria &&
                    selectedCategoria !== availableCategorie[0]
                      ? i.categoria === selectedCategoria
                      : true,
                  ),
                ).map(categoria =>
                  getGironi(
                    iscrizioniGironi.filter(
                      i =>
                        i.categoria === categoria &&
                        (selectedGirone === availableGironi[0]
                          ? true
                          : i.girone === selectedGirone),
                    ),
                    true,
                  ).map(girone => (
                    <Grid key={`${categoria}-${girone}`} size={12}>
                      <GironiTable categoria={categoria} girone={girone} />
                    </Grid>
                  )),
                )}
              </Grid>
            ))}
          <DialogGirone
            open={openChangeGirone}
            handleClose={handleCloseChangeGirone}
            triggerFunction={changeGirone}
            team={girIscrizione}
            iscrizioniGironi={iscrizioniGironi}
          />
        </>
      )}
    </Grid>
  )
}

export default Gironi
